import { ConflictWhenMerging } from "@common/dto/financialAccount/mergeFinancialAccounts/merge-financial-accounts-response.dto";
import { StandardModalBody } from "@components/general/Modal/Modal";
import { ModalComponentWithResultProps } from "@helpers/modal";
import { useState } from "react";
import { Button } from "@components/general/Button/Button";
import { BothModifiedConflictResolving } from "./BothModifiedConflictResolving";
import { MultipleMatchingConflictResolving } from "./MultipleMatchingConflictResolving";

export interface AdditionalMergeConflictModalResult {
    bothModifiedConflictResolution?: number;
    multipleMatchingConflictResolution?: {
        fromDestination: number;
        toKeep: number;
    };
}

interface Props
    extends ModalComponentWithResultProps<AdditionalMergeConflictModalResult> {
    conflict: ConflictWhenMerging;
}

export const AdditionalMergeConflictModal: React.FC<Props> = ({
    conflict,
    close,
}) => {
    const { bothModifiedConflict, multipleMatchingConflict } = conflict;
    const [bothModifiedConflictResolution, setBothModifiedConflictResolution] =
        useState<number | undefined>(undefined);
    const [
        multipleMatchingConflictResolution,
        setMultipleMatchingConflictResolution,
    ] = useState<
        | {
              fromDestination: number;
              toKeep: number;
          }
        | undefined
    >(undefined);

    return (
        <StandardModalBody>
            <p>
                Additional merge conflict detected, please resolve it before
                continuing merge.{" "}
                {bothModifiedConflict &&
                    "Following transactions were modified in both accounts. Please select which version to keep."}
                {multipleMatchingConflict &&
                    "Following transaction from destination account have more than one matching transaction in account to be merged. Can't determine which transactions should be moved."}
            </p>

            {bothModifiedConflict && (
                <BothModifiedConflictResolving
                    conflict={bothModifiedConflict}
                    resolutions={
                        bothModifiedConflictResolution
                            ? [bothModifiedConflictResolution]
                            : []
                    }
                    onResolve={(_, transaction) => {
                        setBothModifiedConflictResolution(transaction.id);
                    }}
                />
            )}

            {multipleMatchingConflict && (
                <MultipleMatchingConflictResolving
                    conflict={multipleMatchingConflict}
                    resolutions={
                        multipleMatchingConflictResolution
                            ? [multipleMatchingConflictResolution]
                            : []
                    }
                    onResolve={([resolution]) => {
                        setMultipleMatchingConflictResolution(resolution);
                    }}
                />
            )}

            <footer className="d-flex justify-content-between">
                <Button variant="secondary" onClick={() => close()}>
                    Abort merge
                </Button>
                <Button
                    variant="default"
                    disabled={
                        !bothModifiedConflictResolution &&
                        !multipleMatchingConflictResolution
                    }
                    onClick={() =>
                        close({
                            bothModifiedConflictResolution,
                            multipleMatchingConflictResolution,
                        })
                    }
                >
                    Continue
                </Button>
            </footer>
        </StandardModalBody>
    );
};
